import { useCallback } from "react";
import { useUsers } from "../../hooks/useUsers";
import { useInvitedUsers } from "../../hooks/useInvitedUsers";
import { useUpdateUser } from "../../hooks/useUpdateUser";
import { useDeleteUser } from "../../hooks/useDeleteUser";
import { useResendInvitation } from "../../hooks/useResendInvitation";
import { Button } from "../../components/ui/button";
import UsersTable from "./components/UsersTable";
import InvitedUsersTable from "./components/InvitedUsersTable";
import CreateUserDialog from "./components/CreateUserDialog";
import InviteUserDialog from "./components/InviteUserDialog";
import { Plus, Mail } from "lucide-react";
import { toast } from "sonner";
import type { User } from "@/generated/types";

export default function Users() {
  const { users, loading: usersLoading, refetch: refetchUsers } = useUsers();
  const { invitedUsers, loading: invitedLoading, refetch: refetchInvited } = useInvitedUsers();

  const { updateUser: deactivateUser } = useUpdateUser({
    onCompleted: () => {
      toast.success("Usuário desativado com sucesso.");
      refetchUsers();
    },
  });
  const { updateUser: activateUser } = useUpdateUser({
    onCompleted: () => {
      toast.success("Usuário ativado com sucesso. Uma senha temporária foi enviada para o e-mail dele.");
      refetchUsers();
    },
  });
  const { deleteUser } = useDeleteUser({
    onCompleted: () => {
      toast.success("Convite excluído com sucesso.");
      refetchInvited();
    },
  });
  const { resendInvitation } = useResendInvitation({
    onCompleted: () => {
      toast.success("Convite reenviado com sucesso.");
      refetchInvited();
    },
  });

  const handleDeactivate = useCallback((id: string) => {
    deactivateUser(id, new Date().toISOString());
  }, [deactivateUser]);

  const handleActivate = useCallback((id: string) => {
    activateUser(id, null);
  }, [activateUser]);

  const handleDeleteInvitation = useCallback((id: string) => {
    deleteUser(id);
  }, [deleteUser]);

  const handleResendInvitation = useCallback((user: User) => {
    resendInvitation(user.email);
  }, [resendInvitation]);

  return (
    <div className="container-elegant py-24">
      <div className="mb-12 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-light tracking-tight text-primary-950 dark:text-white">
            Usuários
          </h1>
          <p className="mt-2 text-primary-600 dark:text-primary-400">
            Gerenciar contas, funções e acesso de usuários
          </p>
        </div>
        <div className="flex gap-3">
          <InviteUserDialog onSuccess={refetchInvited}>
            <Button
              variant="outline"
              className="rounded-none uppercase tracking-widest text-xs border-primary-200 dark:border-primary-800"
            >
              <Mail className="h-4 w-4 mr-2" />
              Convidar Usuário
            </Button>
          </InviteUserDialog>
          <CreateUserDialog onSuccess={refetchUsers}>
            <Button className="rounded-none uppercase tracking-widest text-xs">
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Usuário
            </Button>
          </CreateUserDialog>
        </div>
      </div>

      <UsersTable
        users={users}
        loading={usersLoading}
        onDeactivate={handleDeactivate}
        onActivate={handleActivate}
        onEditSuccess={refetchUsers}
      />

      <InvitedUsersTable
        invitedUsers={invitedUsers}
        loading={invitedLoading}
        onDelete={handleDeleteInvitation}
        onResend={handleResendInvitation}
      />
    </div>
  );
}
